/**
 * Roman Urdu detection.
 *
 * Drivers on this network overwhelmingly type Urdu in Latin script on
 * their phones ("mujhe Riyadh se truck chahiye"), not in Nastaʿlīq. The
 * script heuristic in `resolve-agent-language.ts` reads any Latin text
 * as English, so a Roman-Urdu driver was being answered in English on
 * every turn. This closes that gap.
 *
 * The signal is a set of high-frequency Urdu/Hindustani function words
 * that essentially never appear as standalone English words —
 * pronouns, question words, the copula, and the handful of verbs that
 * carry a freight request. We match on whole words only, and a single
 * hit is enough: these tokens are distinctive, and a message mixing one
 * Urdu function word with English nouns ("truck kitne ka hai") is still
 * an Urdu speaker to answer in Urdu.
 *
 * Deliberately excluded: greeting/agreement tokens (`salam`, `haan`,
 * `ji`, `ok`) — they are handled as language-neutral elsewhere and are
 * too weak to carry a language decision on their own.
 */

/**
 * Whole-word Roman-Urdu markers. Kept lowercase; matched case-insensitively
 * against word-boundaried tokens. Spelling varies wildly in Roman Urdu, so
 * common variants are listed explicitly rather than stemmed.
 */
const ROMAN_URDU_MARKERS: ReadonlySet<string> = new Set([
  // pronouns / possessives
  'mujhe', 'mujhy', 'muje', 'mera', 'meri', 'mere', 'hamara', 'hamari',
  'tumhara', 'tumhari', 'aap', 'apko', 'aapko', 'apka', 'aapka', 'unko', 'usko',
  // question words
  'kya', 'kia', 'kyun', 'kyu', 'kiun', 'kaise', 'kaisay', 'kaisa', 'kahan',
  'kahaan', 'kab', 'kitna', 'kitne', 'kitni', 'kaun', 'kon',
  // copula / very common verbs + auxiliaries
  'hai', 'hain', 'hei', 'hy', 'nahi', 'nahin', 'nai', 'karna', 'karo', 'kardo',
  'krna', 'kro', 'krdo', 'karein', 'karen', 'kardein', 'chahiye', 'chahiay',
  'chaiye', 'chahye', 'bhejo', 'bhej', 'bhejna', 'bhejdo', 'dedo',
  'dena', 'lena', 'raha', 'rahe', 'rahi', 'hoga', 'hogi', 'honge', 'hogaya',
  'chalega', 'chalegi', 'mila', 'milega', 'milegi', 'batao', 'bataye', 'bata',
  // freight / everyday nouns + adjectives that are unmistakably Urdu
  'gari', 'gaari', 'gadi', 'acha', 'accha', 'theek', 'thik', 'thk', 'abhi',
  'kal', 'paisa', 'paise', 'paisay', 'jaldi', 'shukriya', 'zaroorat', 'zarurat',
]);

/**
 * True when the (Latin-script) text carries at least one distinctive
 * Roman-Urdu marker word. Caller is responsible for having already
 * decided the text is Latin-dominant; this only answers "Urdu or
 * English?" for such text.
 */
export const looksLikeRomanUrdu = (text: string): boolean => {
  const tokens = text.toLowerCase().match(/[a-z]+/g);
  if (!tokens) return false;
  return tokens.some(token => ROMAN_URDU_MARKERS.has(token));
};
